import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Mail,
  Phone,
  BadgeCheck,
  UserCircle2,
  CalendarDays,
  IdCard,
  Clock,
  Copy,
  ExternalLink,
  Power,
  ChevronRight,
  Briefcase,
  Monitor,
  Search,
  FileText,
  User,
  MailCheck,
  PhoneCall,
  Play,
  Pause,
  MessageSquare,
} from "lucide-react";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
// import ConvertLeadToDeal from "../../../convert-lead/convert-lead-to-deal";
import { Alert, Skeleton, Snackbar } from '@mui/material';
import Tooltip from "../../../../../component/ui/tooltip";
import { GlobalContext } from "../../../../../context/Context";
import CommentsSection from "../../../../../component/comment";
import ConvertLeadToDeal from "../../../../../component/modals/convert-lead/convert-lead-to-deal";


const tabs = [
  { key: "summary", label: "Summary" },
  { key: "about", label: "About" },
  { key: "comments", label: "Comments" },
  { key: "call", label: "Calls" },
  { key: "emails", label: "Emails" },
  { key: "sms", label: "Sms" },
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span className={classNames("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", tones[tone])}>
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-sm hover:shadow-md">
      <div className="flex items-start gap-3">
        {/* <div className="rounded-xl bg-slate-50 p-2">
          <Icon className="size-5" />
        </div> */}
        <div className="min-w-0">
          <div className="text-xs text-slate-500 capitalize">{title.includes("_") ? title.split("_").map(word => ` ${word}`) : title}</div>
          {/* truncate */}
          <div className="text-lg font-semibold tracking-tight">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
      {/* {tone !== "default" && (
        <div className="absolute right-3 top-3">
          <Badge tone={tone}>{tone === "green" ? "Good" : tone === "amber" ? "Watch" : "Check"}</Badge>
        </div>
      )} */}
    </div>
  );
}

function Field({ label, value, icon: Icon, copyable, link }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid grid-cols-12 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50">
      <div className="col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-500">
        {Icon ? <Icon className="size-4" /> : null}
        {label}
      </div>
      <div className="col-span-8 flex items-center justify-between gap-2 text-[13px] text-slate-800">
        {link ? (
          <a href={link} className="truncate underline decoration-slate-300 underline-offset-2 hover:text-blue-600 hover:decoration-blue-400">
            {value}
          </a>
        ) : (
          <span className="truncate">{value}</span>
        )}
        {copyable && (
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(String(value ?? ""));
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch { }
            }}
            className="inline-flex items-center gap-1 rounded-md border border-solid border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
          >
            <Copy className="size-3.5" /> {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function LeadQuickView({ open = true, onClose = () => { }, data, ProductList, PackageList, DealStages, SubProductList, fetchSubProducts = () => { }, fetchPackages = () => { } }) {

  // console.log("Data", data)
  const [lead, setLead] = useState({})
  const [activeTab, setActiveTab] = useState("summary");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(lead?.leadstatus);
  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);







  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertFormData, setConvertFormData] = useState({
    deal_product: "",
    deal_package: "",
    estimated_payment_date: "",
    dealamount: "",
    dealstage: "",
    dealprobability: "",
    description: ""
  });

  // console.log("data", data)


  const GetData = async (id) => {

    let apiData = JSON.stringify({
      "leadid": id
    });

    try {
      let call = await apiCall({ method: 'get', url: `leads/get-lead/${id}`, data: apiData });
      let leadData = call?.data

      // console.log("leadData", leadData)
      setIsLoading(true);

      let activity = (leadData?.recent_activity)?.map(item => {
        const dateObj = new Date(item.time);
        const formattedDate = dateObj.toISOString().slice(0, 16).replace("T", " ");
        // This gives "YYYY-MM-DD HH:MM"

        return {
          ts: formattedDate,
          text: item.data
        };
      });


      const sdata = {
        id: leadData?.leadid,
        name: leadData?.name,
        phone: leadData?.phone,
        email: leadData?.email,
        service: leadData?.product_name,
        type: `${leadData?.leadtype?.charAt(0)?.toUpperCase()}${leadData?.leadtype?.slice(1)}`,
        LeadSource: leadData?.leadsource,
        AssignedTo: leadData?.assigned_user_details ?? "-",
        Subject: leadData?.subject,
        Message: leadData?.message,
        LeadStatus: leadData?.leadstatus,
        CreatedTime: leadData?.createdtime,
        stats: leadData?.stats,
        updated_at: leadData?.updated_at,
        country: leadData?.country,
        state: leadData?.state,
        city: leadData?.city,
        brand: leadData?.lead_brand?.brandname,

        // client_deals: client_deals,

      };

      setStatus(leadData?.leadstatus);
      setLead(sdata);
      setIsLoading(true)
    } catch (error) {
      console.warn("Error : ", error)
    }
  }

  const handleConvertLead = async (id, formData) => {
    try {
      let response = await apiCall({
        method: "post",
        url: `leads/convert-lead/${id}`,
        data: {
          name: lead?.name,
          ...formData
        },
      });

      if (response?.success) {
        alert("Lead successfully converted to contact ✅");
      } else {
        alert("Failed to convert lead");
      }
    } catch (error) {
      console.error("Error converting lead:", error);
      alert("Something went wrong");
    }
  };







  // useOnClickOutside(containerRef, () => onClose());

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // console.log("lead in fields", lead);

  const fields = useMemo(() => [




    { tab: "summary", label: "ID", value: lead?.id ?? "-", icon: IdCard },
    { tab: "summary", label: "Name", value: lead?.name ?? "-", icon: User },
    // { tab: "summary", label: "Status", value: status, icon: BadgeCheck },
    { tab: "summary", label: "Email", value: lead?.email ?? "-", icon: Mail, copyable: true, link: `mailto:${lead?.email}` },
    { tab: "summary", label: "Phone", value: lead?.phone ?? "-", icon: Phone, copyable: true, link: `tel:${lead?.phone}` },
    { tab: "summary", label: "Product", value: lead?.service ?? "-", icon: Briefcase },
    // { tab: "summary", label: "Type", value: lead?.type ?? "-", icon: FileText },
    // { tab: "summary", label: "Brand", value: lead?.brand ?? "-", icon: ExternalLink },
    {
      tab: "summary",
      label: "Assigned",
      value: (
        <div className="flex -space-x-2">
          {Array.isArray(lead?.AssignedTo) && lead.AssignedTo.length > 0 ? (
            lead.AssignedTo.map((user, idx) => (
              <Tooltip key={idx} content={user.name} position="top center">
                <div
                  className={`relative ${idx !== 0 ? "-ml-0" : ""}`}
                  // style={{
                  //   zIndex: lead.AssignedTo.length - idx,
                  // }}
                >
                  {user?.userimg ? (
                    <img
                      src={user.userimg}
                      alt={user.name}
                      className="h-8 w-8 rounded-full border-2 border-white object-cover object-center shadow-sm bg-white"
                      onError={(e) => {
                        e.target.onerror = null; // prevent infinite loop
                        e.target.style.display = "none";
                        const fallback = document.createElement("div");
                        fallback.className =
                          "h-8 w-8 flex items-center justify-center rounded-full bg-gray-300 text-black text-xs font-medium border-2 border-white shadow-sm";
                        fallback.innerText = (
                          (user?.name?.split(" ")[0]?.[0] || "") +
                          (user?.name?.split(" ")[1]?.[0] || "")
                        ).toUpperCase();
                        e.target.parentElement.appendChild(fallback);
                      }}
                    />
                  ) : (
                    <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-300 text-black text-xs font-medium border-2 border-white shadow-sm">
                      {(user?.name?.split(" ")[0]?.[0] || "") +
                        (user?.name?.split(" ")[1]?.[0] || "")}
                    </div>
                  )}
                </div>
              </Tooltip>
            ))
          ) : (
            <span className="text-gray-400 text-sm">-</span>
          )}
        </div>
      ),
      icon: UserCircle2,
    },





    { tab: "about", label: "Subject", value: lead?.Subject ?? "-", icon: ExternalLink },
    { tab: "about", label: "Message", value: lead?.Message ?? "-", icon: ExternalLink },
    { tab: "about", label: "Country", value: lead?.country ?? "-", icon: ExternalLink },
    { tab: "about", label: "State", value: lead?.state ?? "-", icon: ExternalLink },
    { tab: "about", label: "City", value: lead?.city ?? "-", icon: ExternalLink },


  ], [lead, status]);




  const filteredFields = fields.filter(f =>
    (!query || `${f.label} ${f.value}`.toLowerCase().includes(query.toLowerCase())) &&
    (activeTab === "summary" ? ["summary", "work", "personal"].includes(f.tab) : f.tab === activeTab)
  );


  useEffect(() => {
    GetData(data?.leadid)
  }, [data])

  const [alertData, setAlertData] = useState({ isShow: false })



  const ConvertDeal = () => {
    setShowConvertModal(true);
    // DealProduct();
    // DealPackage();
    // DealStage();
  }


  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message })
    setShowConvertModal(false);

  }

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message })
  }

  const handleClose = () => {
    setAlertData({ isShow: false })
  }

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.aside
              ref={containerRef}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative mt-auto mr-[2vh] mb-auto ml-0 flex h-[96vh] w-[800px] max-w-[98vw] flex-col overflow-hidden rounded-3xl border border-solid border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-xl"
              aria-modal
              role="dialog"
            >
              {
                isLoading ? (
                  <>
                    {/* Header */}
                    <div className="border-b border-solid border-slate-200 bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                      <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                        <div className="flex min-w-0 items-center gap-3">
                          {/* <img
                          src={lead?.avatar}
                          alt={lead?.name}
                          className="size-12 rounded-2xl object-cover ring-2 ring-white shadow"
                        /> */}
                          <div className="flex items-center justify-center size-12 bg-slate-200 rounded-full overflow-hidden">
                            <p className='capitalize font-semi-bold text-base text-black'>
                              {lead?.name?.split(" ")?.map((n, i) => n.charAt(0))}
                            </p>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h2 className="truncate text-lg font-semibold tracking-tight text-slate-900">
                                {lead?.name}
                              </h2>
                              {/* <Badge tone={(status == "Pending") ? "blue" : (status == "Approved") ? "green" : "red"} icon={BadgeCheck}>{status}</Badge> */}
                              {/* <Badge tone="blue" icon={Briefcase}>{lead?.role}</Badge> */}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                              <span className="inline-flex items-center gap-1"><MailCheck className="size-4" /> {lead?.email}</span>

                              <span className="inline-flex items-center gap-1"><PhoneCall className="size-4" /> {lead?.phone}</span>


                              {/* <span className="inline-flex items-center gap-1"><PhoneCall className="size-4" /> {lead?.phone}</span> */}

                            </div>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            className="inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                            onClick={ConvertDeal}
                          >
                            <Power className="size-4" /> Convert to Deal
                          </button>

                          {/* <button to={`/leads/lead/${data.leadid}`}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        // onClick={() => window.alert("Opening full profile… (wire to route)")}
                        >
                          <ExternalLink className="size-4" /> View Full Profile
                        </button> */}


                          <button
                            className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                            onClick={onClose}
                            aria-label="Close"
                          >
                            <X className="size-4" /> Exit
                          </button>
                        </div>
                      </div>

                      {/* Quick actions */}
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        <QuickAction icon={Mail} label="Email" href={`mailto:${lead?.email}`} />
                        <QuickAction icon={Phone} label="Call" href={`tel:${lead?.phone}`} />

                        {/* <QuickAction
                          icon={Power}
                          label="Convert Lead to Deal"
                          onClick={ConvertDeal}
                        /> */}

                        {/* <QuickAction icon={MessageSquare} label="Message" onClick={() => window.alert("Open internal chat")} />
                      <QuickAction icon={PencilLine} label="Edit" onClick={() => window.alert("Open edit form")} />
                      <QuickAction
                        icon={Power}
                        label={status === "Active" ? "Deactivate" : "Activate"}
                       onClick={() => setStatus(s => (s === "Active" ? "Inactive" : "Active"))}
                      /> */}


                        <div className="ml-auto flex items-center gap-2">
                          <kbd className="rounded-md border border-solid bg-white px-1.5 py-1 text-[10px] text-slate-500">⌘/Ctrl</kbd>
                          <span className="text-[10px] text-slate-500">+ F to search</span>
                        </div>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 w-full">
                      {/* Left: main content */}
                      <div className="col-span-12 flex min-h-0 flex-col gap-4">
                        {/* Top stats */}
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">

                          {
                            lead?.stats?.map((ele, idx) => {
                              return (
                                <Stat title={ele?.key} value={ele?.value} icon={CalendarDays} />
                              )
                            })
                          }
                        </div>

                        {/* Tabs */}
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-1">
                            {tabs?.map(t => (
                              <button
                                key={t.key}
                                onClick={() => setActiveTab(t.key)}
                                className={classNames(
                                  "rounded-md px-[12px] py-[8px] text-sm font-medium border border-solid border-[#E1E4EA]",
                                  activeTab === t.key
                                    ? "bg-[#F5F5F5] text-[#0E121B]"
                                    : "text-slate-600 hover:bg-slate-100"
                                )}
                              >
                                {t.label}
                              </button>
                            ))}
                          </div>
                          <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-slate-400" />
                            <input
                              id="quickview-search"
                              placeholder="Search fields…"
                              value={query}
                              onChange={e => setQuery(e.target.value)}
                              className="w-64 rounded-2xl border border-solid border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900/10"
                            />
                          </div>
                        </div>

                        <div className="h-full overflow-auto rounded-2xl py-2">
                          {/* Render fields for current tab (or summary) */}
                          <div className="grid grid-cols-1 gap-3">
                            {filteredFields?.map((f, idx) => (
                              <Field key={idx} {...f} />
                            ))}

                          </div>



                          {/* {activeTab === "call" && <CallsTab />} */}



                          {activeTab === "comments" && (
                            <CommentsSection
                              type="lead"
                              id={lead?.id}
                              apiBaseUrl="lead"
                              setAlertData={setAlertData}
                            />

                          )}











                          {activeTab === "call" && (
                            <TabPlaceholder
                              icon={PhoneCall}
                              title="Calls"
                              message="We are working on the call logs and recordings section."
                            />
                          )}

                          {activeTab === "emails" && (
                            <TabPlaceholder
                              icon={Mail}
                              title="Emails"
                              message="Email activity and history will be available soon."
                            />
                          )}

                          {activeTab === "sms" && (
                            <TabPlaceholder
                              icon={MessageSquare}
                              title="SMS"
                              message="SMS communication history is under development."
                            />
                          )}


                        </div>
                      </div>

                      {
                        showConvertModal && (
                          <ConvertLeadToDeal
                            leadData={lead}
                            ProductList={ProductList}
                            PackageList={PackageList}
                            SubProductList={SubProductList}
                            fetchSubProducts={fetchSubProducts}
                            fetchPackages={fetchPackages}
                            DealStages={DealStages}
                            onClose={() => { setShowConvertModal(false); }} onError={onError} onSubmit={onSuccess}
                          />





                          // <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[100]">
                          //   <div className="bg-white p-6 rounded-lg w-[450px]">
                          //     <h2 className="text-lg font-bold mb-4">Convert Lead to Deal</h2>

                          //     <label className="block mb-2">Deal Product</label>
                          //     <input
                          //       className="border p-2 mb-4 w-full"
                          //       value={convertFormData.deal_product}
                          //       onChange={e => setConvertFormData({ ...convertFormData, deal_product: e.target.value })}
                          //     />

                          //     <label className="block mb-2">Deal Package</label>
                          //     <input
                          //       className="border p-2 mb-4 w-full"
                          //       value={convertFormData.deal_package}
                          //       onChange={e => setConvertFormData({ ...convertFormData, deal_package: e.target.value })}
                          //     />

                          //     <label className="block mb-2">Estimated Payment Date</label>
                          //     <input
                          //       type="date"
                          //       className="border p-2 mb-4 w-full"
                          //       value={convertFormData.estimated_payment_date}
                          //       onChange={e => setConvertFormData({ ...convertFormData, estimated_payment_date: e.target.value })}
                          //     />

                          //     <label className="block mb-2">Deal Amount</label>
                          //     <input
                          //       type="number"
                          //       className="border p-2 mb-4 w-full"
                          //       value={convertFormData.dealamount}
                          //       onChange={e => setConvertFormData({ ...convertFormData, dealamount: e.target.value })}
                          //     />

                          //     <label className="block mb-2">Deal Stage</label>
                          //     <input
                          //       className="border p-2 mb-4 w-full"
                          //       value={convertFormData.dealstage}
                          //       onChange={e => setConvertFormData({ ...convertFormData, dealstage: e.target.value })}
                          //     />

                          //     <label className="block mb-2">Deal Probability (%)</label>
                          //     <input
                          //       type="number"
                          //       className="border p-2 mb-4 w-full"
                          //       value={convertFormData.dealprobability}
                          //       onChange={e => setConvertFormData({ ...convertFormData, dealprobability: e.target.value })}
                          //     />

                          //     <label className="block mb-2">Description</label>
                          //     <textarea
                          //       className="border p-2 mb-4 w-full"
                          //       value={convertFormData.description}
                          //       onChange={e => setConvertFormData({ ...convertFormData, description: e.target.value })}
                          //     />

                          //     <div className="flex justify-end gap-4">
                          //       <button
                          //         className="bg-gray-300 px-4 py-2 rounded"
                          //         onClick={() => setShowConvertModal(false)}
                          //       >
                          //         Cancel
                          //       </button>

                          //       <button
                          //         className="text-white px-4 py-2 rounded" style={{ backgroundColor: "#2563EB" }}
                          //         onClick={async () => {
                          //           await handleConvertLead(lead?.id, convertFormData);
                          //           setShowConvertModal(false);
                          //           setConvertFormData({
                          //             deal_product: "",
                          //             deal_package: "",
                          //             estimated_payment_date: "",
                          //             dealamount: "",
                          //             dealstage: "",
                          //             dealprobability: "",
                          //             description: ""
                          //           });
                          //         }}
                          //       >
                          //         Save
                          //       </button>
                          //     </div>
                          //   </div>
                          // </div>


                        )
                      }

                      {/* Right: context column */}
                      {/* <div className="col-span-12 flex min-h-0 flex-col gap-4 lg:col-span-4">
                      <div className="rounded-2xl border border-solid border-slate-200 bg-white p-4">
                        <h3 className="mb-3 flex items-center justify-between text-sm font-semibold">
                          Highlights
                          <Badge tone="blue">Real-time</Badge>
                        </h3>
                        <ul className="space-y-2 text-sm text-slate-700">
                          <li className="flex items-start gap-2"><ArrowUpRight className="mt-0.5 size-4" /> Completed 9/9 hours today</li>
                          <li className="flex items-start gap-2"><ShieldCheck className="mt-0.5 size-4" /> No policy flags in last 90 days</li>
                          <li className="flex items-start gap-2"><Monitor className="mt-0.5 size-4" /> All assets compliant</li>
                        </ul>
                      </div>

                      <div className="rounded-2xl border border-solid border-slate-200 bg-white p-4">
                        <h3 className="mb-3 text-sm font-semibold">Tags</h3>
                        <div className="flex flex-wrap gap-2">
                          {lead?.tags?.map(t => (
                            <span key={t} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{t}</span>
                          ))}
                          <button className="rounded-full border border-dashed border-slate-300 px-3 py-1 text-xs text-slate-500 hover:bg-slate-50">+ Add</button>
                        </div>
                      </div>

                      <div className="min-h-0 flex-1 overflow-auto rounded-2xl border border-solid border-slate-200 bg-white p-4">
                        <h3 className="mb-3 text-sm font-semibold">Recent Activity</h3>
                        <Activity items={lead?.activity} compact />
                      </div>
                    </div> */}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <IdCard className="size-4" /> ID {data?.leadid}
                        {/* <span>-</span>
                      <CalendarDays className="size-4" />  {lead?.updated_at} */}
                      </div>
                    </div>
                  </>
                ) : (
                  <InnerLoader wrapperClass={'w-full'} />
                )
              }

            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence >


      <Snackbar open={alertData?.isShow} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>

    </>
  );
}


function QuickAction({ icon: Icon, label, href, onClick }) {
  const className = "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
  if (href) return (
    <a className={className} href={href}>
      <Icon className="size-4" /> {label}
    </a>
  );
  return (
    <button className={className} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}



function TabPlaceholder({ icon: Icon, title, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-300 rounded-2xl bg-white">
      <div className="flex items-center justify-center size-14 rounded-full bg-slate-100 mb-4">
        <Icon className="size-6 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{message}</p>
      <div className="mt-4 px-4 py-2 text-xs text-slate-400 border border-solid border-slate-200 rounded-full bg-slate-50">
        🚧 Under Development
      </div>
    </div>
  );
}
